/*
 * Leeff: toegankelijkheid bovenop Prestige. Bewust los van theme.js, zodat een theme-update dit niet overschrijft.
 *
 * 1. Zoeken en het mobiele menu zijn dialogen die de focus alleen verplaatsen op apparaten met hover. Op touch en
 *    na een klik bleef de focus achter de dialoog staan: getypte tekst kwam nergens terecht en een schermlezer bleef
 *    op de pagina eronder. Nu gaat de focus na het openen naar het zoekveld of de eerste menulink.
 * 2. Het aantal gevonden producten bij het zoeken wordt via een statusregel voorgelezen. Het zoekveld is een gewoon
 *    zoekveld (geen combobox meer): de resultaten zijn links en tabs, geen keuzelijst met pijltoetsen.
 */
(() => {
  const focusNaOpenen = (dialoog, doel) => {
    dialoog.addEventListener('dialog:after-show', () => {
      if (dialoog.contains(document.activeElement)) return;
      doel()?.focus({ preventScroll: true });
    });
  };

  const volgZoekresultaten = (zoeken, veld) => {
    const resultaten = zoeken.querySelector('predictive-search');
    const melding = zoeken.querySelector('[data-leeff-zoekmelding]');
    if (!veld || !resultaten || !melding) return;

    let timer;

    const bijwerken = () => {
      const heeftZoekterm = veld.value.trim() !== '';
      const heeftInhoud = resultaten.textContent.trim() !== '';

      if (!heeftZoekterm) {
        melding.textContent = '';
        return;
      }

      if (!heeftInhoud) return;

      const producten = new Set(
        Array.from(resultaten.querySelectorAll('a[href*="/products/"]'), (link) => link.pathname)
      ).size;

      if (producten === 0) {
        melding.textContent = melding.dataset.geen;
      } else if (producten === 1) {
        melding.textContent = melding.dataset.een;
      } else {
        melding.textContent = melding.dataset.meer.replace('999', producten);
      }
    };

    const later = (ms) => {
      clearTimeout(timer);
      timer = setTimeout(bijwerken, ms);
    };

    new MutationObserver(() => later(500)).observe(resultaten, { childList: true, subtree: true });
    veld.addEventListener('input', () => later(800));
  };

  // 3. De aankondigingsbalk wisselt elke paar seconden van bericht. Bewegende inhoud moet te pauzeren zijn (WCAG
  //    2.2.2): de balk staat stil zolang de muis erop staat of de toetsenbordfocus erin zit.
  const pauzeerAankondiging = () => {
    document.querySelectorAll('announcement-bar-carousel').forEach((carrousel) => {
      const balk = carrousel.closest('.announcement-bar') || carrousel;
      const stil = () => carrousel.player?.pause();
      const verder = () => {
        if (balk.matches(':hover') || balk.contains(document.activeElement)) return;
        carrousel.player?.resume();
      };

      balk.addEventListener('pointerenter', stil);
      balk.addEventListener('pointerleave', verder);
      balk.addEventListener('focusin', stil);
      balk.addEventListener('focusout', () => setTimeout(verder, 0));
    });
  };

  // 4. Pauzeknop voor de film in de slideshow (snippet in sections/slideshow.liquid). Heeft de bezoeker gepauzeerd,
  //    of vraagt het systeem om minder beweging, dan blijft de video stil, ook als het theme hem zelf weer start.
  const bedienFilm = () => {
    const minderBeweging = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    document.querySelectorAll('.leeff-video-pauze').forEach((houder) => {
      const dia = houder.closest('.slideshow__slide') || houder.parentElement;
      const pauzeKnop = houder.querySelector('[data-leeff-video="pauzeren"]');
      const speelKnop = houder.querySelector('[data-leeff-video="afspelen"]');
      // gepauzeerd is wat de bezoeker wil; de knop toont wat de video echt doet (autoplay kan geblokkeerd zijn).
      let gepauzeerd = minderBeweging;

      const zichtbareVideo = () =>
        Array.from(dia.querySelectorAll('video')).find((video) => video.offsetWidth > 0) || dia.querySelector('video');

      const toonStand = () => {
        const video = zichtbareVideo();
        const speelt = Boolean(video && !video.paused);
        pauzeKnop.hidden = !speelt;
        speelKnop.hidden = speelt;
      };

      houder.addEventListener('click', (event) => {
        const knop = event.target.closest('[data-leeff-video]');
        if (!knop) return;

        event.preventDefault();
        event.stopPropagation();

        gepauzeerd = knop === pauzeKnop;
        dia.querySelectorAll('video').forEach((video) => (gepauzeerd ? video.pause() : video.play().catch(() => {})));
        setTimeout(() => {
          toonStand();
          (pauzeKnop.hidden ? speelKnop : pauzeKnop).focus();
        }, 100);
      });

      // play en pause bubbelen niet; met capture vangen we ze wel op de dia.
      dia.addEventListener('play', (event) => {
        if (gepauzeerd) event.target.pause();
        toonStand();
      }, true);
      dia.addEventListener('pause', toonStand, true);

      if (gepauzeerd) {
        dia.querySelectorAll('video').forEach((video) => video.pause());
      }

      toonStand();
    });
  };

  // 5. Tabs in het blok Momenten: alleen de actieve tab zit in de tabvolgorde (pijltoetsen wisselen tussen tabs),
  //    zodat Tab niet langs elke tab afzonderlijk loopt.
  const tabvolgordeMomenten = () => {
    document.querySelectorAll('.leeff-momenten').forEach((tabs) => {
      const zet = () => {
        tabs.querySelectorAll('[role="tab"]').forEach((tab) => {
          tab.tabIndex = tab.getAttribute('aria-selected') === 'true' ? 0 : -1;
        });
      };

      new MutationObserver(zet).observe(tabs, { attributes: true, subtree: true, attributeFilter: ['aria-selected'] });
      zet();
    });
  };

  // 6. De andere dialogen (filterlade, sorteren, taalkeuze, snel kopen) hadden hetzelfde probleem als zoeken en menu:
  //    zonder hover bleef de focus op de knop en liep Tab achter de dialoog door. Na het openen gaat de focus nu naar
  //    het doel dat de dialoog zelf opgeeft, anders naar de eerste knop of link. Is dat een tekstveld, dan krijgt de
  //    dialoog zelf de focus, zodat op een telefoon niet ongevraagd het toetsenbord opent.
  const FOCUSBAAR = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, summary, [tabindex]:not([tabindex="-1"])';
  const TEKSTVELD = 'input:not([type="checkbox"], [type="radio"], [type="submit"], [type="button"]), textarea, select';

  const focusInDialogen = () => {
    // dialog:after-show bubbelt niet; met capture komt hij toch langs document.
    document.addEventListener('dialog:after-show', (event) => {
      const dialoog = event.target;
      if (!(dialoog instanceof HTMLElement) || dialoog.matches('header-search, header-sidebar')) return;
      if (dialoog.getAttribute('initial-focus') === 'false') return;

      setTimeout(() => {
        if (dialoog.contains(document.activeElement)) return;

        const zichtbaar = (el) => el && el.getClientRects().length > 0;
        const gewenst = dialoog.initialFocus;
        let doel = zichtbaar(gewenst) ? gewenst : Array.from(dialoog.querySelectorAll(FOCUSBAAR)).find(zichtbaar);

        if (doel && !doel.matches(TEKSTVELD)) {
          doel.focus({ preventScroll: true });
        }

        // Geen geschikt doel, of het liet zich niet focussen (bijvoorbeeld in een dichtgeklapt filter): dan de
        // dialoog zelf, net als Prestige doet op apparaten met hover.
        if (!dialoog.contains(document.activeElement)) {
          if (!dialoog.hasAttribute('tabindex')) dialoog.setAttribute('tabindex', '-1');
          dialoog.focus({ preventScroll: true });
        }
      }, 0);
    }, true);
  };

  // 7. Filteren, sorteren en bladeren laden de productlijst opnieuw. Het element met de focus verdween daarbij, zodat
  //    de focus bovenaan de pagina belandde. Nu gaat hij terug naar de knop waarmee de lade of het sorteermenu werd
  //    geopend, of na bladeren naar het eerste product. Het nieuwe aantal producten wordt voorgelezen.
  const volgCollectie = () => {
    const sectie = document.querySelector('[id^="product-list-"]')?.closest('.shopify-section');
    if (!sectie) return;

    const melding = document.createElement('p');
    melding.className = 'sr-only';
    melding.setAttribute('role', 'status');
    melding.dataset.leeffCollectiemelding = '';
    document.body.append(melding);

    let laatst = null;
    document.addEventListener('focusin', (event) => (laatst = event.target));

    const herstel = () => {
      const aantal = sectie.querySelector('.collection-toolbar__products-count')?.textContent.trim();
      if (aantal) {
        melding.textContent = '';
        setTimeout(() => (melding.textContent = aantal), 100);
      }

      if (document.activeElement && document.activeElement !== document.body) return;

      let doel = null;
      if (laatst?.closest('#facets-drawer, [aria-controls="facets-drawer"]')) {
        doel = sectie.querySelector('[aria-controls="facets-drawer"]');
      } else if (laatst?.closest('#sort-by-popover, [aria-controls="sort-by-popover"]')) {
        doel = sectie.querySelector('[aria-controls="sort-by-popover"]');
      } else if (laatst?.closest('.pagination, [class*="pagination"]')) {
        // de productnaam, niet de fotolink ervoor, zodat een schermlezer meteen het eerste product noemt
        const kaart = sectie.querySelector('[id^="product-list-"] product-card, [id^="product-list-"] .product-card');
        doel = Array.from(kaart?.querySelectorAll('a[href*="/products/"]') || []).find((link) => link.textContent.trim());
      }

      doel?.focus({ preventScroll: true });
    };

    new MutationObserver(() => setTimeout(herstel, 50)).observe(sectie, { childList: true });
  };

  // 8. Het mobiele menu opent submenu's in een tweede paneel. Dat paneel is een dialoog en heeft nu als naam de titel
  //    van het submenu dat erin openstaat.
  const benoemSubmenu = () => {
    document.querySelectorAll('header-sidebar-collapsible-panel').forEach((paneel) => {
      const zet = () => {
        const open = paneel.querySelector('.header-sidebar__sub-panel:not([hidden])') || paneel.querySelector('.header-sidebar__sub-panel');
        const titel = open?.querySelector('.header-sidebar__back-button')?.textContent.trim();
        if (titel) paneel.setAttribute('aria-label', titel);
      };

      new MutationObserver(zet).observe(paneel, { attributes: true, subtree: true, attributeFilter: ['hidden'] });
      zet();
    });
  };

  // 9. De vergroting van productfoto's (PhotoSwipe) is een dialoog zonder naam. Die krijgt de productnaam en
  //    aria-modal, zodat een schermlezer weet dat de pagina eronder even niet meedoet.
  const benoemVergroting = () => {
    const galerij = document.querySelector('product-gallery[data-leeff-lightbox]');
    if (!galerij) return;

    new MutationObserver(() => {
      document.querySelectorAll('.pswp:not([aria-label])').forEach((venster) => {
        venster.setAttribute('aria-label', galerij.dataset.leeffLightbox);
        venster.setAttribute('aria-modal', 'true');
      });
    }).observe(document.body, { childList: true });
  };

  // 10. Rijen die op een telefoon zijwaarts scrollen (teamfoto's, productgalerij) moeten met het toetsenbord te
  //     bereiken zijn. Alleen als er echt iets te scrollen valt, komen ze in de tabvolgorde.
  const scrollbareRijen = () => {
    const rijen = document.querySelectorAll('multi-column.scroll-area[role="region"], scroll-carousel.product-gallery__carousel');
    if (!rijen.length) return;

    const zet = (rij) => {
      if (rij.scrollWidth > rij.clientWidth + 1) {
        rij.tabIndex = 0;
      } else {
        rij.removeAttribute('tabindex');
      }
    };

    const waarnemer = new ResizeObserver((items) => items.forEach((item) => zet(item.target)));
    rijen.forEach((rij) => waarnemer.observe(rij));
  };

  // 11. De gewone winkelwagen laadde bij elke aantalswijziging de hele pagina opnieuw (WCAG 3.2.2) en de focus belandde
  //     daarna bovenaan (2.4.3). Nu werken we alleen de winkelwagensectie bij, zetten we de focus terug op dezelfde
  //     regel en melden we wat er veranderde (4.1.3). De B2B-winkelwagen regelt dit zelf.
  const winkelwagen = () => {
    const sectie = document.querySelector('.shopify-section--main-cart');
    if (!sectie || !document.querySelector('[data-leeff-winkelwagen]') || document.querySelector('[data-b2b-line-item]')) return;

    const sectieId = sectie.id.replace('shopify-section-', '');
    // De teksten staan in de winkelwagensectie zelf; bij een lege winkelwagen is dat blok weg, dus we onthouden ze.
    const teksten = Object.assign({}, document.querySelector('[data-leeff-winkelwagen]').dataset);
    const melding = document.createElement('p');
    melding.className = 'sr-only';
    melding.setAttribute('role', 'status');
    melding.dataset.leeffCartmelding = '';
    document.body.append(melding);

    const zeg = (tekst) => {
      if (!tekst) return;
      melding.textContent = '';
      setTimeout(() => (melding.textContent = tekst), 100);
    };

    // Elke regel heeft twee aantalvelden (een voor de brede weergave, een voor de smalle). Alleen het zichtbare veld
    // kan de focus krijgen.
    const velden = () => Array.from(sectie.querySelectorAll('[name="updates[]"][data-line-key]'))
      .filter((veld) => veld.getClientRects().length > 0);

    const focusTerug = (sleutel, plek) => {
      const zelfde = sleutel && velden().find((veld) => veld.dataset.lineKey === sleutel);
      if (zelfde) return zelfde.focus({ preventScroll: true });
      const lijst = velden();
      if (lijst.length) return lijst[Math.min(plek, lijst.length - 1)].focus({ preventScroll: true });
      const kop = sectie.querySelector('h1');
      if (kop) {
        kop.tabIndex = -1;
        kop.focus({ preventScroll: true });
      }
    };

    const totaalTekst = () => {
      const bedragen = sectie.querySelectorAll('.cart-recap .text-lg');
      return bedragen.length ? bedragen[bedragen.length - 1].textContent.trim() : '';
    };

    const wijzig = async (sleutel, regelnummer, aantal, bron) => {
      const regel = bron && (bron.closest('tr') || bron.closest('line-item'));
      const titel = regel?.querySelector('.line-item-info a, .line-item-info p')?.textContent.trim() || '';
      const plek = Math.max(velden().findIndex((veld) => veld.dataset.lineKey === sleutel), 0);

      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
      let winkelwagenData = null;
      try {
        const verzoek = { quantity: aantal, sections: sectieId };
        if (sleutel) {
          verzoek.id = sleutel;
        } else {
          verzoek.line = parseInt(regelnummer, 10);
        }
        const antwoord = await fetch(window.Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(verzoek)
        });
        winkelwagenData = await antwoord.json();
        if (!antwoord.ok) {
          if (bron && bron.name === 'updates[]') bron.value = bron.defaultValue;
          zeg(winkelwagenData.description || winkelwagenData.message || '');
          return;
        }
      } catch (fout) {
        window.location.reload();
        return;
      } finally {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }

      const nieuweHtml = winkelwagenData.sections && winkelwagenData.sections[sectieId];
      if (nieuweHtml) {
        const vers = new DOMParser().parseFromString(nieuweHtml, 'text/html');
        const bronSectie = vers.querySelector('.shopify-section--main-cart') || vers.body;
        sectie.replaceChildren(...document.importNode(bronSectie, true).childNodes);
      }

      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'line-item:change', cart: winkelwagenData }
      }));

      if (winkelwagenData.item_count === 0) {
        zeg(teksten.leeg);
      } else if (aantal === 0) {
        zeg((teksten.verwijderd || '').replace('%s', titel).replace('%t', totaalTekst()));
      } else {
        zeg((teksten.aangepast || '').replace('%n', aantal).replace('%t', totaalTekst()));
      }

      focusTerug(sleutel, plek);
    };

    // Vóór Prestige: dat script herlaadt op de winkelwagenpagina de hele pagina.
    document.addEventListener('change', (event) => {
      const veld = event.target.closest?.('[name="updates[]"][data-line-key]');
      if (!veld || !sectie.contains(veld)) return;
      event.stopPropagation();
      wijzig(veld.dataset.lineKey, null, parseInt(veld.value, 10) || 0, veld);
    }, true);

    document.addEventListener('click', (event) => {
      const link = event.target.closest?.('a[href*="/cart/change"]');
      if (!link || !sectie.contains(link)) return;
      event.preventDefault();
      event.stopPropagation();
      const adres = new URL(link.href, window.location.origin);
      wijzig(adres.searchParams.get('id'), adres.searchParams.get('line'), parseInt(adres.searchParams.get('quantity'), 10) || 0, link);
    }, true);
  };

  const start = () => {
    pauzeerAankondiging();
    bedienFilm();
    tabvolgordeMomenten();
    focusInDialogen();
    volgCollectie();
    benoemSubmenu();
    benoemVergroting();
    scrollbareRijen();
    winkelwagen();

    document.querySelectorAll('header-search').forEach((zoeken) => {
      const veld = zoeken.querySelector('input[name="q"]');
      focusNaOpenen(zoeken, () => veld);
      volgZoekresultaten(zoeken, veld);
    });

    document.querySelectorAll('header-sidebar').forEach((menu) => {
      focusNaOpenen(menu, () => menu.querySelector('.header-sidebar__main-panel :is(a[href], button:not([disabled]))'));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* Cookievoorkeuren opnieuw openen vanuit de footer. Toestemming intrekken hoort net zo makkelijk te
   zijn als geven; in de footer stond alleen een link naar de privacyverklaring. Shopify levert
   privacyBanner.showPreferences(). De regel blijft verborgen tot die methode er werkelijk is, zodat
   er nooit een knop staat die niets doet. */
(function () {
  function koppel() {
    var regel = document.querySelector('[data-leeff-cookievoorkeuren]');
    if (!regel) return;
    var open = window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function';
    if (!open) return;
    regel.hidden = false;
    var knop = regel.querySelector('button');
    if (!knop) return;
    knop.addEventListener('click', function () {
      try { window.privacyBanner.showPreferences(); } catch (e) { /* stil: dan blijft de privacypagina over */ }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', koppel);
  else koppel();
  // De banner laadt soms na de pagina; daarom nog een keer proberen.
  window.setTimeout(koppel, 2500);
})();

/* Zakelijke nieuwsbrief in de footer. Een ingelogde dealer krijgt geen formulier maar een knop:
   zijn adres is bij de backend al bekend, dus hij hoeft niets te typen. Het blok blijft verborgen
   tot de backend bevestigt dat er een lijst is ingesteld, zodat er nooit een knop staat die niets
   kan. De tekst verschilt per groep; welke lijst erbij hoort bepaalt de backend, niet de pagina. */
(function () {
  var blok = document.querySelector('[data-leeff-b2b-nieuwsbrief]');
  if (!blok) return;

  var tekstEl = blok.querySelector('[data-leeff-b2b-nieuwsbrief-tekst]');
  var knop = blok.querySelector('[data-leeff-b2b-nieuwsbrief-knop]');
  var melding = blok.querySelector('[data-leeff-b2b-nieuwsbrief-melding]');
  var teksten = window.leeffB2bNieuwsbriefTeksten || {};

  function vraag(actie) {
    return fetch('/apps/dealer-api', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ action: actie })
    }).then(function (r) { return r.json().catch(function () { return {}; }); });
  }

  vraag('nieuwsbrief-status').then(function (data) {
    if (!data || !data.beschikbaar) return;
    // De groep bepaalt alleen de zin; de lijst kiest de backend.
    tekstEl.textContent = data.groep === 'kerst' ? (teksten.kerst || '') : (teksten.dealer || '');
    blok.hidden = false;
  }).catch(function () { /* stil: dan blijft het blok gewoon weg */ });

  knop.addEventListener('click', function () {
    knop.disabled = true;
    knop.setAttribute('aria-busy', 'true');
    vraag('nieuwsbrief-aanmelden').then(function (data) {
      var stand = data && data.stand;
      if (stand === 'aangemeld' || stand === 'stond_er_al') {
        melding.textContent = stand === 'aangemeld' ? (teksten.gelukt || '') : (teksten.stond_er_al || '');
        knop.hidden = true;
      } else {
        melding.textContent = teksten.mislukt || '';
        knop.disabled = false;
      }
      knop.removeAttribute('aria-busy');
    }).catch(function () {
      melding.textContent = teksten.mislukt || '';
      knop.disabled = false;
      knop.removeAttribute('aria-busy');
    });
  });
})();

/*
 * 3. De nieuwsbriefpopup is een niet-modale dialoog zonder focusval en had daardoor geen Escape-toets. Nu sluit Escape
 *    hem, tenzij er op dat moment een ander venster (menu, zoeken, lade) open staat: dat krijgt de toets eerst.
 */
(() => {
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    if (document.querySelector(':is(header-sidebar, header-search, .drawer, .modal, x-modal, x-drawer)[open]')) return;
    const popup = document.querySelector('newsletter-popup[open]');
    if (popup && typeof popup.hide === 'function') popup.hide();
  });
})();
